"use client"

import * as React from "react"
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  Search, 
  SlidersHorizontal 
} from "lucide-react"
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  section: string
  isLoading: boolean
}

export function DataTable<TData, TValue>({
  columns,
  data,
  section,
  isLoading
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [showFilters, setShowFilters] = React.useState(false)

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  })

  const renderFilters = () => {
    if (!showFilters) return null;

    switch (section) {
      case "users":
        return (
          <div className="bg-white dark:bg-slate-900 p-4 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 mb-4 animate-in fade-in-50 duration-200">
            <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Filter Options</h3>
            <div className="space-y-3">
              <div>
                <label htmlFor="email-filter" className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                  Email
                </label>
                <Input
                  id="email-filter"
                  placeholder="Filter by email..."
                  value={(table.getColumn("email")?.getFilterValue() as string) ?? ""}
                  onChange={(event) => table.getColumn("email")?.setFilterValue(event.target.value)}
                  className="h-9 text-sm"
                />
              </div>
            </div>
          </div>
        );
      case "invoices":
        return (
          <div className="bg-white dark:bg-slate-900 p-4 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 mb-4 animate-in fade-in-50 duration-200">
            <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Filter Options</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label htmlFor="invoice-number-filter" className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                  Invoice Number
                </label>
                <Input
                  id="invoice-number-filter"
                  placeholder="Filter by invoice number..."
                  value={(table.getColumn("invoiceNumber")?.getFilterValue() as string) ?? ""}
                  onChange={(event) => table.getColumn("invoiceNumber")?.setFilterValue(event.target.value)}
                  className="h-9 text-sm"
                />
              </div>
              <div>
                <label htmlFor="customer-filter" className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                  Customer
                </label>
                <Input
                  id="customer-filter"
                  placeholder="Filter by customer name..."
                  value={(table.getColumn("customer")?.getFilterValue() as string) ?? ""}
                  onChange={(event) => table.getColumn("customer")?.setFilterValue(event.target.value)}
                  className="h-9 text-sm"
                />
              </div>
              <div>
                <label htmlFor="status-filter" className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                  Status
                </label>
                <Input
                  id="status-filter"
                  placeholder="Filter by status..."
                  value={(table.getColumn("status")?.getFilterValue() as string) ?? ""}
                  onChange={(event) => table.getColumn("status")?.setFilterValue(event.target.value)}
                  className="h-9 text-sm"
                />
              </div>
            </div>
          </div>
        );
      case "receipts":
        return (
          <div className="bg-white dark:bg-slate-900 p-4 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 mb-4 animate-in fade-in-50 duration-200">
            <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Filter Options</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label htmlFor="receipt-number-filter" className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                  Receipt Number
                </label>
                <Input
                  id="receipt-number-filter"
                  placeholder="Filter by receipt number..."
                  value={(table.getColumn("receiptNumber")?.getFilterValue() as string) ?? ""}
                  onChange={(event) => table.getColumn("receiptNumber")?.setFilterValue(event.target.value)}
                  className="h-9 text-sm"
                />
              </div>
              <div>
                <label htmlFor="client-name-filter" className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                  Client Name
                </label>
                <Input
                  id="client-name-filter"
                  placeholder="Filter by client name..."
                  value={(table.getColumn("clientName")?.getFilterValue() as string) ?? ""}
                  onChange={(event) => table.getColumn("clientName")?.setFilterValue(event.target.value)}
                  className="h-9 text-sm"
                />
              </div>
              <div>
                <label htmlFor="amount-paid-filter" className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                  Amount Paid
                </label>
                <Input
                  id="amount-paid-filter"
                  placeholder="Filter by amount paid..."
                  value={(table.getColumn("amountPaid")?.getFilterValue() as string) ?? ""}
                  onChange={(event) => table.getColumn("amountPaid")?.setFilterValue(event.target.value)}
                  className="h-9 text-sm"
                />
              </div>
            </div>
          </div>
        );
      case "quotations":
        return (
          <div className="bg-white dark:bg-slate-900 p-4 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 mb-4 animate-in fade-in-50 duration-200">
            <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Filter Options</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label htmlFor="quotation-number-filter" className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                  Quotation Number
                </label>
                <Input
                  id="quotation-number-filter"
                  placeholder="Filter by quotation number..."
                  value={(table.getColumn("quotationNumber")?.getFilterValue() as string) ?? ""}
                  onChange={(event) => table.getColumn("quotationNumber")?.setFilterValue(event.target.value)}
                  className="h-9 text-sm"
                />
              </div>
              <div>
                <label htmlFor="client-name-filter" className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                  Client Name
                </label>
                <Input
                  id="client-name-filter"
                  placeholder="Filter by client name..."
                  value={(table.getColumn("clientName")?.getFilterValue() as string) ?? ""}
                  onChange={(event) => table.getColumn("clientName")?.setFilterValue(event.target.value)}
                  className="h-9 text-sm"
                />
              </div>
              <div>
                <label htmlFor="status-filter" className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                  Status
                </label>
                <Input
                  id="status-filter"
                  placeholder="Filter by status..."
                  value={(table.getColumn("status")?.getFilterValue() as string) ?? ""}
                  onChange={(event) => table.getColumn("status")?.setFilterValue(event.target.value)}
                  className="h-9 text-sm"
                />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const getActiveFiltersCount = () => {
    return Object.values(table.getState().columnFilters).length;
  };

  return (
    <Card className="bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
      <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
        <div className="relative w-full md:w-auto">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder={`Search ${section}...`}
            className="pl-9 h-10 w-full md:w-[300px] bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus-visible:ring-blue-500"
            value={(table.getColumn(section === "users" ? "email" : section === "invoices" ? "invoiceNumber" : section === "receipts" ? "receiptNumber" : "quotationNumber")?.getFilterValue() as string) ?? ""}
            onChange={(event) =>
              table.getColumn(section === "users" ? "email" : section === "invoices" ? "invoiceNumber" : section === "receipts" ? "receiptNumber" : "quotationNumber")?.setFilterValue(event.target.value)
            }
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="h-10 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filters
          {getActiveFiltersCount() > 0 && (
            <Badge className="ml-1 bg-blue-500 hover:bg-blue-600 text-white">{getActiveFiltersCount()}</Badge>
          )}
        </Button>
      </div>

      {renderFilters()}

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50 dark:bg-slate-800/50">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="border-b border-slate-200 dark:border-slate-700 hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead 
                      key={header.id}
                      className="h-11 px-4 text-xs font-medium text-slate-500 dark:text-slate-400"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-[400px] text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-3" />
                      <p className="text-sm text-slate-500 dark:text-slate-400">Loading data...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                        className="border-b border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id} className="p-4 text-sm">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="h-[400px] text-center">
                        <div className="flex flex-col items-center justify-center">
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">No results found</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">Try adjusting your search or filters</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-800">
        <div className="text-sm text-slate-500 dark:text-slate-400">
          Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{" "}
          {Math.min(
            (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
            table.getFilteredRowModel().rows.length
          )}{" "}
          of {table.getFilteredRowModel().rows.length} entries
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="h-8 w-8 p-0 flex items-center justify-center border-slate-200 dark:border-slate-700"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-sm font-medium">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="h-8 w-8 p-0 flex items-center justify-center border-slate-200 dark:border-slate-700"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                className="h-8 border-slate-200 dark:border-slate-700"
              >
                {table.getState().pagination.pageSize} per page
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <DropdownMenuItem
                  key={pageSize}
                  onClick={() => table.setPageSize(pageSize)}
                  className="cursor-pointer"
                >
                  {pageSize} per page
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </Card>
  )
}
